// Componente „view" rendate de MainApp: Tratament (doze, medicamente, evenimente,
// simptome) + formularele modale pentru celelalte tab-uri (condiții, medici, valori,
// familie, cont). Extrase din App.js; primesc totul prin props.
import React, { useState, useEffect } from "react";
import { t } from './i18n';
import { fmAlert } from './dialog';
import { fmtD, todayISO, dkStr, isoD, icsEscape, getDIM, getFDOW, confirmDelete, shouldMedBeActiveOnDate } from './helpers';
import { buildDayLog, isHistoricDose } from './logic';
import { TimeInput, IconTrash, Dropdown, DatePicker, SearchableSelect, DateField, CaregiverBadge, PhoneInput, getInitials } from './ui';
import { CAREGIVER_PALETTE, DOSE_FORMS, KINSHIP, MONTHS, MSHORT, SPECS, SYMPTOMS_LIST, WDAYS } from './data';
import MOCK_MEDS from './meds';

// Emoji în funcție de forma farmaceutică
const FORM_EMOJI = {
  "Comprimată":"💊","Comprimată filmat":"💊","Comprimată efervescentă":"💊","Comprimată sublinguală":"💊",
  "Capsulă":"💊","Capsulă cu eliberare prelungită":"💊",
  "Fiolă":"💉","Soluție injectabilă":"💉","Perfuzie":"🩸",
  "Picături orale":"💧","Picături oftalmice":"👁","Picături auriculare":"👂",
  "Spray nazal":"👃","Spray bucal":"💨","Spray cutanat":"🌿",
  "Plasture":"🩹","Supozitor":"⬛","Sirop":"🍶","Suspensie orală":"🍶",
  "Granule":"⚪","Pulbere":"⚪","Cremă":"🧴","Unguent":"🧴","Gel":"🧴","Spumă":"🧴",
  "Ovul":"⚪","Inhaler":"💨","Nebulizator":"💨","Alta":"💊"
};
function getFormEmoji(form) { return FORM_EMOJI[form] || "💊"; }

// Tipuri de eveniment (culori/etichete) pentru programări
const ET = { consult:{label:"Consult",color:"var(--blue)",bg:"var(--blue-bg)"}, analize:{label:"Analize",color:"var(--amber)",bg:"var(--amber-bg)"}, tratament:{label:"Tratament",color:"var(--green)",bg:"#0a3020"}, urgenta:{label:"Urgență",color:"var(--red)",bg:"var(--red-bg-soft)"} };

// Hook: păstrează un draft de text în localStorage (supraviețuiește reîncărcării/blocării)
function useDraftState(key, initial="") {
  const storageKey = "fm_draft_" + key;
  const [val, setVal] = React.useState(()=>{
    try { const saved = localStorage.getItem(storageKey); return saved!==null ? saved : initial; }
    catch(e){ return initial; }
  });
  React.useEffect(()=>{
    try { if(val) localStorage.setItem(storageKey, val); } catch(e){}
  }, [val, storageKey]);
  const clear = React.useCallback(()=>{ try{ localStorage.removeItem(storageKey); }catch(e){} }, [storageKey]);
  return [val, setVal, clear];
}

// Zilele săptămânii (obiecte Date) pentru un offset de săptămâni față de cea curentă
const getWeekDays = off => {
  const t=new Date();
  const dow=t.getDay()===0?6:t.getDay()-1;
  const mon=new Date(t.getFullYear(),t.getMonth(),t.getDate()-dow+off*7,12,0,0);
  return Array.from({length:7},(_,i)=>{ const d=new Date(mon.getFullYear(),mon.getMonth(),mon.getDate()+i,12,0,0); return d; });
};

// Generează un fișier .ics (eveniment de calendar) pentru o programare
function addAppointmentToCalendar(ev, reminderHours) {
  if(!ev.date){ fmAlert(t("Programarea nu are dată.")); return; }
  const time = ev.time || "09:00";
  const dtStart = ev.date.replace(/-/g,"") + "T" + time.replace(":","") + "00";
  const [h,mn] = time.split(":").map(Number);
  const end = new Date(2000,0,1,h,(mn||0)+30);
  const dtEnd = ev.date.replace(/-/g,"") + "T" + String(end.getHours()).padStart(2,"0") + String(end.getMinutes()).padStart(2,"0") + "00";
  const title = ev.title || ev.spec || "Programare medicală";
  const loc = [ev.hospital, ev.address].filter(Boolean).join(", ");
  const desc = [ev.dr&&("Medic: "+ev.dr), ev.phone&&("Tel: "+ev.phone), ev.visitNote].filter(Boolean).join("\n");
  const stamp = new Date().toISOString().replace(/[-:]/g,"").split(".")[0] + "Z";
  const alarm = reminderHours>0
    ? `BEGIN:VALARM\nACTION:DISPLAY\nDESCRIPTION:${icsEscape(title)}\nTRIGGER:-PT${reminderHours}H\nEND:VALARM\n`
    : "";
  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Fisa Medicala//RO\nCALSCALE:GREGORIAN\nBEGIN:VEVENT\nUID:fm-${Date.now()}@fisa-medicala\nDTSTAMP:${stamp}\nDTSTART:${dtStart}\nDTEND:${dtEnd}\nSUMMARY:${icsEscape(title)}\nLOCATION:${icsEscape(loc)}\nDESCRIPTION:${icsEscape(desc)}\n${alarm}END:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([ics], {type:"text/calendar;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `programare-${ev.date}.ics`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(url), 1000);
}

export function NoteInput({initialValue, onSave}) {
  const [val, setVal] = React.useState(initialValue||"");
  // No sync needed - component remounts via key when dose changes
  return (
    <textarea
      value={val}
      onChange={e=>setVal(e.target.value)}
      onBlur={e=>onSave(e.target.value)}
      placeholder={t("Notă...")}
      rows={2}
      style={{width:"100%",fontSize:12,padding:"4px 8px",background:"var(--bg)",
        border:"1px solid var(--border-2)",borderRadius:6,color:"var(--text)",
        resize:"none",fontFamily:"inherit"}}/>
  );
}

export function DoseSection({meds, selLog, selDay, todayK, setDoseStatus, updateDoseField, setMedLogs, setMeds, medLogs, onSaveMedLog, setAllDoneForSlot, family, readOnly=false}) {
  const nowTime = new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false}).padStart(5,"0");
  const isToday = selDay === todayK;
  const deletedMedLabel = t("Medicament șters");
  const pendingSlots = {};
  const resolvedItems = [];

  // Iterăm peste medicamentele care AU doze în log-ul zilei.
  // Reguli de afișare per zi:
  //  - dacă medicamentul e programat în acea zi (shouldMedBeActiveOnDate) → afișează
  //  - dacă NU (ex. după dezactivare) dar dozele sunt deja confirmate/sărite → afișează (istoric)
  //  - dacă NU și dozele sunt doar pending → nu afișa (planul a dispărut din acea zi)
  Object.keys(selLog||{}).forEach(medIdKey=>{
    const med = meds.find(m=>String(m.id)===String(medIdKey));
    const dayLog = selLog[medIdKey]||[];
    // Medicament șters complet din listă: afișează doar intrările de istoric (confirmate sau manuale)
    if(!med){
      dayLog.forEach((dose,di)=>{
        if(!isHistoricDose(dose)) return;
        const ghostMed = {id:medIdKey, name:dose.medName||deletedMedLabel, color:"var(--text-3)", _deleted:true};
        const t = dose.time||"--:--";
        if(dose.status==="pending"){
          if(!pendingSlots[t]) pendingSlots[t]=[];
          pendingSlots[t].push({dose,med:ghostMed,di});
        } else {
          resolvedItems.push({dose,med:ghostMed,di,t});
        }
      });
      return;
    }
    const scheduledThisDay = shouldMedBeActiveOnDate(med, selDay);
    dayLog.forEach((dose,di)=>{
      const isHist = isHistoricDose(dose);
      // afișează doza dacă medicamentul e programat azi, SAU doza e de istoric (confirmată/manuală)
      if(!scheduledThisDay && !isHist) return;
      const t = dose.time||"--:--";
      if(dose.status==="pending") {
        if(!pendingSlots[t]) pendingSlots[t]=[];
        pendingSlots[t].push({dose,med,di});
      } else {
        resolvedItems.push({dose,med,di,t});
      }
    });
  });

  const sortedPending = Object.entries(pendingSlots).sort(([a],[b])=>{
    const aH=parseInt(a.split(":")[0],10)||0,bH=parseInt(b.split(":")[0],10)||0;
    if((aH<6?1:0)!==(bH<6?1:0)) return (aH<6?1:0)-(bH<6?1:0);
    return a.localeCompare(b);
  });
  const sortedResolved = resolvedItems.sort((a,b)=>{
    const aH=parseInt(a.t.split(":")[0],10)||0,bH=parseInt(b.t.split(":")[0],10)||0;
    if((aH<6?1:0)!==(bH<6?1:0)) return (aH<6?1:0)-(bH<6?1:0);
    return a.t.localeCompare(b.t);
  });

  if(sortedPending.length===0&&sortedResolved.length===0)
    return <div style={{textAlign:"center",color:"var(--text-4)",fontSize:13,padding:"20px 0",fontStyle:"italic"}}>{t("Nimic programat pe astăzi")}</div>;

  return (
    <div>
      {sortedPending.map(([time,items])=>{
        const th=parseInt(time.split(":")[0],10)||0;
        const isOverdue=isToday&&time!=="--:--"&&time.padStart(5,"0")<=nowTime&&th>=6;
        const hasMultiple = items.length > 1;
        return (
          <div key={time} style={{marginBottom:12}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
              <div style={{fontFamily:"monospace",fontSize:16,fontWeight:400,color:isOverdue?"var(--red)":"var(--blue)"}}>{time}</div>
              <div style={{flex:1,height:1,background:"var(--border)"}}/>
              {!readOnly&&hasMultiple&&(
                <button
                  onClick={()=>setAllDoneForSlot(selDay, items)}
                  style={{background:"var(--green-bg)",border:"none",borderRadius:7,color:"var(--green)",cursor:"pointer",padding:"4px 10px",fontSize:12,fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>
                  ✓ {t("Toate")} ({items.length})
                </button>
              )}
            </div>
            {items.map(({dose,med,di})=>(
              <DoseRow key={med.id+"-"+dose.schedIdx} dose={dose} med={med} di={di} isOverdue={isOverdue}
                selDay={selDay} setDoseStatus={setDoseStatus} updateDoseField={updateDoseField}
                setMedLogs={setMedLogs} setMeds={setMeds} onSaveMedLog={onSaveMedLog} family={family} readOnly={readOnly}/>
            ))}
          </div>
        );
      })}
      {sortedResolved.length>0&&(
        <ResolvedSection count={sortedResolved.length}>
          {sortedResolved.map(({dose,med,di})=>(
            <DoseRow key={med.id+"-"+dose.schedIdx+"-r"} dose={dose} med={med} di={di} showTime={true}
              selDay={selDay} setDoseStatus={setDoseStatus} updateDoseField={updateDoseField}
              setMedLogs={setMedLogs} setMeds={setMeds} onSaveMedLog={onSaveMedLog} family={family} readOnly={readOnly}/>
          ))}
        </ResolvedSection>
      )}
    </div>
  );
}

export function DoseRow({dose,med,di,showTime,isOverdue,selDay,setDoseStatus,updateDoseField,setMedLogs,setMeds,onSaveMedLog,family,readOnly=false}) {
  const [expanded,setExpanded] = React.useState(false);
  const [swipeX,setSwipeX] = React.useState(0);
  const [startX,setStartX] = React.useState(null);
  const [swiping,setSwiping] = React.useState(false);
  const [doseText,setDoseText] = React.useState(String(dose.dose||"1"));
  // Intenționat doar pe `expanded`: reîncărcăm valoarea dozei la deschidere, nu în timp ce scrii.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(()=>{ if(expanded) setDoseText(String(dose.dose||"1")); },[expanded]);
  const isDone=dose.status==="done";
  const isSkipped=dose.status==="skipped";
  const isPending=dose.status==="pending";
  const THRESHOLD=80;

  function onTouchStart(e){if(readOnly)return;setStartX(e.touches[0].clientX);setSwiping(false);}
  function onTouchMove(e){
    if(readOnly||startX===null)return;
    const dx=e.touches[0].clientX-startX;
    if(Math.abs(dx)>10)setSwiping(true);
    setSwipeX(Math.max(-140,Math.min(140,dx)));
  }
  function onTouchEnd(){
    if(readOnly)return;
    if(swipeX>THRESHOLD){setDoseStatus(selDay,med.id,dose.schedIdx,"done");setExpanded(false);}
    else if(swipeX<-THRESHOLD){setDoseStatus(selDay,med.id,dose.schedIdx,"skipped");setExpanded(false);}
    setSwipeX(0);setStartX(null);setSwiping(false);
  }

  return (
    <div style={{position:"relative",marginBottom:2}}>
      <div style={{position:"absolute",inset:0,borderRadius:8,
        pointerEvents:"none",
        background:swipeX>THRESHOLD?"var(--green-bg)":swipeX<-THRESHOLD?"var(--border)":"transparent",
        display:"flex",alignItems:"center",justifyContent:swipeX>0?"flex-start":"flex-end",padding:"0 20px"}}>
        <span style={{fontSize:22,fontWeight:700,color:swipeX>THRESHOLD?"var(--green)":"var(--text-2)",
          opacity:Math.min(1,Math.abs(swipeX)/THRESHOLD)}}>
          {swipeX>THRESHOLD?"✓":swipeX<-THRESHOLD?"—":""}
        </span>
      </div>
      <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onClick={()=>{if(!readOnly&&!swiping)setExpanded(v=>!v);}}
        style={{transform:`translateX(${swipeX}px)`,transition:swipeX===0?"transform 0.3s":"none",
          padding:"8px 4px",cursor:"pointer",opacity:isSkipped?0.5:1,
          position:"relative",background:"var(--bg)",borderRadius:8}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:400,textDecoration:isSkipped?"line-through":"none",
              color:isSkipped?"var(--text-4)":isDone?"var(--text-2)":isOverdue?"var(--red)":"var(--text)"}}>
              <span style={{fontSize:14,marginRight:4}}>{getFormEmoji(med.form)}</span>
              {med.name}
              <span style={{color:"var(--text-3)",fontSize:12,marginLeft:8}}>× {dose.dose||"1"}</span>
              {showTime&&<span style={{fontFamily:"monospace",color:"var(--blue)",fontSize:12,marginLeft:8}}>{dose.time}</span>}
            </div>
            {dose.note&&<div style={{fontSize:11,color:"var(--text-4)",fontStyle:"italic"}}>{dose.note}</div>}
          </div>
          <CaregiverBadge addedBy={dose.addedBy} addedByName={dose.addedByName} addedByEmail={dose.addedByEmail} family={family}/>
        </div>
      </div>
      {expanded&&(
        <div onClick={e=>e.stopPropagation()} style={{padding:"10px 12px",background:"var(--surface)",borderRadius:"0 0 8px 8px",marginBottom:2}}>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            {isPending&&<>
              <button onClick={()=>{setDoseStatus(selDay,med.id,dose.schedIdx,"done");setExpanded(false);}}
                style={{flex:1,background:"var(--green-bg)",border:"none",borderRadius:7,color:"var(--green)",cursor:"pointer",padding:"7px",fontSize:13,fontFamily:"inherit"}}>{t("✓ Confirmat")}</button>
              <button onClick={()=>{setDoseStatus(selDay,med.id,dose.schedIdx,"skipped");setExpanded(false);}}
                style={{flex:1,background:"var(--border)",border:"none",borderRadius:7,color:"var(--text-2)",cursor:"pointer",padding:"7px",fontSize:13,fontFamily:"inherit",textDecoration:"line-through"}}>{t("Sărit")}</button>
            </>}
            {!isPending&&<>
              <button onClick={()=>{setDoseStatus(selDay,med.id,dose.schedIdx,"pending");setExpanded(false);}}
                style={{flex:1,background:"var(--border)",border:"none",borderRadius:7,color:"var(--text-2)",cursor:"pointer",padding:"7px",fontSize:13,fontFamily:"inherit"}}>{t("↩ Înapoi")}</button>
              <button onClick={()=>{setDoseStatus(selDay,med.id,dose.schedIdx,dose.status==="done"?"skipped":"done");setExpanded(false);}}
                style={{flex:1,background:"var(--border)",border:"none",borderRadius:7,color:"var(--text-2)",cursor:"pointer",padding:"7px",fontSize:13,fontFamily:"inherit"}}>{dose.status==="done"?"— "+t("Sărit"):"✓ "+t("Confirmat")}</button>
            </>}
            <button className="bdel" onClick={()=>confirmDelete(t("Ștergi această doză?"),()=>{
              const qty=parseFloat(dose.dose)||1;
              // Dacă doza era confirmată (luată), întoarcem stocul înapoi DIRECT (doza se șterge, deci nu mai e consumată)
              if(dose.status==="done" && setMeds){
                setMeds(ms=>ms.map(m=>String(m.id)===String(med.id)?{...m,stock:Math.max(0,(m.stock||0)+qty)}:m));
              }
              setMedLogs(p=>{
                const log=JSON.parse(JSON.stringify(p[selDay]||{}));
                if(log[med.id]) log[med.id]=log[med.id].filter((_,j)=>j!==di);
                if(onSaveMedLog) onSaveMedLog(selDay, log);
                return{...p,[selDay]:log};
              });
              setExpanded(false);
            })}><IconTrash/></button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div>
              <div className="lbl" style={{fontSize:10,marginBottom:3}}>{t("Ora")}</div>
              <TimeInput value={dose.time} onChange={v=>updateDoseField(selDay,med.id,dose.schedIdx,"time",v)}/>
            </div>
            <div>
              <div className="lbl" style={{fontSize:10,marginBottom:3}}>{t("Doză")}</div>
              <input type="number" min="0.5" step="0.5" value={doseText}
                onChange={e=>setDoseText(e.target.value)}
                onBlur={e=>{
                  const v=parseFloat(e.target.value);
                  if(!isNaN(v)&&v>0) updateDoseField(selDay,med.id,dose.schedIdx,"dose",String(v));
                  else setDoseText(String(dose.dose||"1"));
                }}
                style={{width:"100%",textAlign:"center"}}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <NoteInput key={dose.schedIdx}
                initialValue={dose.note||""}
                onSave={val=>updateDoseField(selDay,med.id,dose.schedIdx,"note",val)}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ResolvedSection({children, count}) {
  const [open, setOpen] = useState(false);
  const total = count ?? React.Children.count(children);
  return (
    <div style={{marginTop:16}}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        display:"flex",alignItems:"center",gap:8,width:"100%",
        background:"none",border:"none",cursor:"pointer",padding:"8px 0",fontFamily:"inherit"
      }}>
        <div style={{flex:1,height:1,background:"var(--border)"}}/>
        <span style={{fontSize:12,color:"var(--text-4)",whiteSpace:"nowrap"}}>{t("Rezolvat")} ({total})</span>
        <div style={{flex:1,height:1,background:"var(--border)"}}/>
        <span style={{fontSize:11,color:"var(--text-4)",marginLeft:4}}>{open?"▲":"▼"}</span>
      </button>
      {open&&<div style={{marginTop:6}}>{children}</div>}
    </div>
  );
}




export function TratamentTab({meds,setMeds,medLogs,setMedLogs,todayK,events,setEvents,symptoms,setSymptoms,setDoseStatus,setModal,weeklySummary,onWeeklySummary,selDay,setSelDay,onDeleteMed,onDeleteSymptom,onSaveEvent,onSaveMedLog,onSaveMed,onToggleActive,setAllDoneForSlot,session,family,readOnly=false}) {
  const [subTab,setSubTabState] = useState(()=>localStorage.getItem('treatmentSubTab')||"calendar");
  function setSubTab(t){ setSubTabState(t); localStorage.setItem('treatmentSubTab',t); }
  const [weekOff,setWeekOff] = useState(0);
  const [calView,setCalView] = useState("week"); // "week" | "month"
  const [calYear,setCalYear] = useState(new Date().getFullYear());
  const [calMonth,setCalMonth] = useState(new Date().getMonth());
  const [showPicker,setShowPicker] = useState(false);
  const [editingEventId,setEditingEventId] = useState(null);

  const weekDays = getWeekDays(weekOff);
  const selLog = medLogs[selDay]||{};
  const selEvents = events[selDay]||[];
  const selSymptoms = symptoms.filter(s=>s.date===selDay);

  function ensureLog(d) {
    setMedLogs(p => {
      const existing = p[d] || {};
      const expected = buildDayLog(meds, d);
      const todayStr = todayISO();
      const isPastDay = d < todayStr;

      // ISTORIC IMUABIL: zilele din trecut nu se modifică niciodată — păstrează tot ce există.
      if(isPastDay && Object.keys(existing).length > 0) {
        return p;
      }

      const activeMedIds = new Set(meds.filter(m=>m.active).map(m=>String(m.id)));
      const cleaned = {};
      Object.entries(existing).forEach(([medId, doses]) => {
        const hasHistoric = (doses||[]).some(isHistoricDose);
        // Păstrează medicamentul în log dacă: e activ SAU are intrări de istoric (confirmate/manuale)
        if(activeMedIds.has(medId) || hasHistoric) cleaned[medId] = doses;
      });

      // Adaugă dozele lipsă DOAR pentru medicamentele active programate azi/viitor
      let changed = Object.keys(cleaned).length !== Object.keys(existing).length;
      Object.entries(expected).forEach(([medId, doses]) => {
        const cur = cleaned[medId];
        const scheduledDoses = (cur||[]).filter(x=>!x.note?.includes("manual"));
        if(!cur || scheduledDoses.length === 0) {
          const manual = (cur||[]).filter(x=>x.note?.includes("manual"));
          cleaned[medId] = [...doses, ...manual];
          changed = true;
        }
      });

      return changed ? {...p, [d]: cleaned} : p;
    });
  }
  function selectDay(d) { setSelDay(d); ensureLog(d); }
  function deleteEvent(dayK,id) {
    const upd=(events[dayK]||[]).filter(e=>e.id!==id);
    setEvents(p=>({...p,[dayK]:upd}));
    onSaveEvent(dayK,upd);
  }

  // Asigură log-ul la mount și când se schimbă selDay sau meds
  // Intenționat fără `ensureLog`: efectul reacționează la ziua selectată și numărul de medicamente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if(meds.length>0) ensureLog(selDay); }, [selDay, meds.length]);
  function saveEvent(dayK,ev) {
    const newDay = ev.date || dayK;
    if(newDay !== dayK) {
      // Data s-a schimbat — scoate din ziua veche, adaugă în ziua nouă
      const oldList = (events[dayK]||[]).filter(e=>e.id!==ev.id);
      const newList = [...(events[newDay]||[]), {...ev,editing:false}];
      setEvents(p=>({...p,[dayK]:oldList,[newDay]:newList}));
      onSaveEvent(dayK,oldList);
      onSaveEvent(newDay,newList);
    } else {
      const upd=(events[dayK]||[]).map(e=>e.id===ev.id?{...ev,editing:false}:e);
      setEvents(p=>({...p,[dayK]:upd}));
      onSaveEvent(dayK,upd);
    }
    setEditingEventId(null);
  }
  function updateDoseField(dayK,medId,schedIdx,field,val) {
    let stockDelta = 0;
    setMedLogs(p=>{
      const log=JSON.parse(JSON.stringify(p[dayK]||{}));
      const d=log[medId]?.find(d=>d.schedIdx===schedIdx);
      if(d) {
        // Daca schimbam doza unei doze confirmate, ajustam stocul cu diferenta
        if(field==="dose" && d.status==="done") {
          const oldQty = parseFloat(d.dose)||1;
          const newQty = parseFloat(val)||1;
          stockDelta = oldQty - newQty; // daca creste doza, delta negativ => scade stoc
        }
        d[field]=val;
      }
      // salveaza in DB
      if(onSaveMedLog) onSaveMedLog(dayK, log);
      return{...p,[dayK]:log};
    });
    if(stockDelta !== 0) {
      setMeds(ms=>{
        const updated = ms.map(m=>m.id===medId?{...m,stock:Math.max(0,(m.stock||0)+stockDelta)}:m);
        const med = updated.find(m=>m.id===medId);
        if(med && onSaveMed) onSaveMed(med);
        return updated;
      });
    }
  }

  // Month calendar helpers
  const dim = getDIM(calYear,calMonth);
  const fdow = getFDOW(calYear,calMonth);
  function prevMonth(){ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }
  function nextMonth(){ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }


  return (
    <div>
      <div className="subtabs">
        {[["calendar","📅 "+t("Săptămână")],["plan","💊 "+t("Plan tratament")]].map(([k,l])=>(
          <button key={k} className={`stab${subTab===k?" act":""}`} onClick={()=>setSubTab(k)}>{l}</button>
        ))}
      </div>

      {subTab==="calendar"&&(
        <div>
          <div className="card" style={{padding:"12px 10px"}}>
            {/* WEEK VIEW - ascuns cand luna e deschisa */}
            {calView==="week"&&(
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <button className="bg" style={{padding:"4px 12px",fontSize:14}} onClick={()=>setWeekOff(w=>w-1)}>‹</button>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text-2)"}}>{t(MSHORT[weekDays[0].getMonth()])} {weekDays[0].getDate()} – {t(MSHORT[weekDays[6].getMonth()])} {weekDays[6].getDate()}, {weekDays[0].getFullYear()}</div>
                  <div style={{display:"flex",gap:4}}>
                    <button className="bg" style={{padding:"4px 12px",fontSize:14}} onClick={()=>setWeekOff(w=>w+1)}>›</button>
                    <button className="bg" style={{padding:"4px 12px",fontSize:14}} onClick={()=>{setCalView("month");setCalMonth(weekDays[0].getMonth());setCalYear(weekDays[0].getFullYear());}} title={t("Deschide luna")}>▼</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
                  {weekDays.map((d,i)=>{
                    const diso=isoD(d); const isT=diso===todayK; const isSel=diso===selDay;
                    const hasEv=!!events[diso]?.length;
                    const log=medLogs[diso]; const doses=log?Object.values(log).flat():[];
                    const done=doses.filter(x=>x.status==="done").length; const total=doses.length;
                    return (
                      <div key={i} onClick={()=>selectDay(diso)} style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"7px 3px",borderRadius:9,cursor:"pointer",background:isSel?"var(--blue-bg)":isT?"var(--blue-bg-soft)":"none",border:isT?"1px solid var(--blue)":"1px solid transparent",transition:"all .15s"}}>
                        <div style={{fontSize:9,color:"var(--text-3)",fontFamily:"monospace",marginBottom:2}}>{t(WDAYS[i])}</div>
                        <div style={{fontSize:15,fontWeight:isSel||isT?700:400,color:isSel?"var(--blue)":isT?"var(--blue-2)":"var(--text)"}}>{d.getDate()}</div>
                        {total>0?<div style={{marginTop:3,width:22,height:3,borderRadius:3,background:"var(--border)",overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(done/total*100)}%`,background:done===total?"var(--green)":done>0?"var(--amber)":"transparent"}}/></div>:<div style={{marginTop:3,height:3}}/>}
                        {hasEv&&<div style={{width:5,height:5,borderRadius:"50%",background:"var(--amber)",marginTop:2}}/>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* MONTH VIEW - inlocuieste saptamana cand e deschisa */}
            {calView==="month"&&(
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <button className="bg" style={{padding:"3px 10px",fontSize:13}} onClick={prevMonth}>‹</button>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{t(MONTHS[calMonth])} {calYear}</div>
                  <div style={{display:"flex",gap:4}}>
                    <button className="bg" style={{padding:"3px 10px",fontSize:13}} onClick={nextMonth}>›</button>
                    <button className="bg" style={{padding:"3px 10px",fontSize:13,color:"var(--blue)"}} onClick={()=>setCalView("week")} title={t("Închide luna")}>▲</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
                  {WDAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"var(--text-4)",fontFamily:"monospace",padding:"2px 0"}}>{t(d)}</div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                  {Array(fdow).fill(null).map((_,i)=><div key={"e"+i}/>)}
                  {Array(dim).fill(null).map((_,i)=>{
                    const d=i+1; const diso=dkStr(calYear,calMonth,d);
                    const isT=diso===todayK; const isSel=diso===selDay;
                    const hasEv=!!events[diso]?.length;
                    const log=medLogs[diso]; const doses=log?Object.values(log).flat():[];
                    const done=doses.filter(x=>x.status==="done").length; const total=doses.length;
                    return (
                      <div key={d} onClick={()=>selectDay(diso)} style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"5px 2px",borderRadius:7,cursor:"pointer",background:isSel?"var(--blue-bg)":isT?"var(--blue-bg-soft)":"none",border:isT?"1px solid var(--blue)":"1px solid transparent",transition:"all .15s",minHeight:34}}>
                        <div style={{fontSize:13,fontWeight:isSel||isT?700:400,color:isSel?"var(--blue)":isT?"var(--blue-2)":"var(--text)"}}>{d}</div>
                        {total>0&&<div style={{width:18,height:2,borderRadius:2,background:"var(--border)",overflow:"hidden",marginTop:2}}><div style={{height:"100%",width:`${Math.round(done/total*100)}%`,background:done===total?"var(--green)":done>0?"var(--amber)":"transparent"}}/></div>}
                        {hasEv&&<div style={{width:4,height:4,borderRadius:"50%",background:"var(--amber)",marginTop:1}}/>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,position:"relative"}}>
            <div className="sec" style={{margin:0}}>{selDay===todayK?t("Astăzi"):fmtD(selDay)}</div>
            {!readOnly&&<div style={{position:"relative"}}>
              <button className="bp" style={{fontSize:12,padding:"7px 14px"}} onClick={()=>setShowPicker(v=>!v)}>+ {t("Adăugare")} ▾</button>
              {showPicker&&(
                <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:12,padding:6,zIndex:50,minWidth:240,boxShadow:"0 8px 32px rgba(0,0,0,.6)"}}>
                  {[
                    {icon:"💊",label:"Medicament",action:()=>{setShowPicker(false);setModal({type:"addMedDose",data:{selDay}});}},
                    {icon:"📅",label:"Programare medicală",action:()=>{setModal({type:"addEvent",data:{date:selDay}});setShowPicker(false);}},
                    {icon:"🤒",label:"Simptom",action:()=>{setModal({type:"addSymptom"});setShowPicker(false);}},
                    {icon:"📝",label:"Notiță",action:()=>{setModal({type:"addNote",data:{selDay}});setShowPicker(false);}},
                  ].map(({icon,label,action})=>(
                    <button key={label} onClick={action} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",color:"var(--text)",padding:"9px 12px",cursor:"pointer",borderRadius:8,fontSize:13,fontFamily:"inherit"}}>
                      <span style={{fontSize:16}}>{icon}</span>{t(label)}
                    </button>
                  ))}
                </div>
              )}
            </div>}
          </div>

          {selEvents.map(ev=>{
            const et=ev.isNote?{label:t("Notiță"),color:"var(--text-2)",bg:"var(--border)"}:ET.consult;
            if(editingEventId===ev.id) return (
              <div key={ev.id} className="card" style={{borderLeft:`3px solid ${et.color}`,marginBottom:8}}>
                <EditEventForm ev={{...ev,date:ev.date||selDay}} onSave={updated=>saveEvent(selDay,updated)} onCancel={()=>setEditingEventId(null)}/>
              </div>
            );
            return (
              <EventRow key={ev.id} ev={ev} et={et} family={family} readOnly={readOnly}
                onEdit={ev.isNote
                  ? (text)=>{ const upd=(events[selDay]||[]).map(e=>e.id===ev.id?{...e,visitNote:text}:e); setEvents(p=>({...p,[selDay]:upd})); onSaveEvent(selDay,upd); }
                  : ()=>setEditingEventId(ev.id)}
                onDelete={()=>confirmDelete(undefined,()=>deleteEvent(selDay,ev.id))}/>
            );
          })}

          {selSymptoms.map(s=>(
            <SymptomRow key={s.id} symptom={s} family={family} readOnly={readOnly} onEdit={()=>setModal({type:"editSymptom",data:s})} onDelete={()=>confirmDelete(undefined,()=>onDeleteSymptom(s))}/>
          ))}

          <DoseSection
            meds={meds} selLog={selLog} selDay={selDay} todayK={todayK}
            medLogs={medLogs}
            setDoseStatus={setDoseStatus} updateDoseField={updateDoseField}
            setMedLogs={setMedLogs} setMeds={setMeds}
            onSaveMedLog={onSaveMedLog}
            setAllDoneForSlot={setAllDoneForSlot}
            family={family}
            readOnly={readOnly}
          />
        </div>
      )}

      {subTab==="plan"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div className="sec" style={{margin:0}}>{t("Plan de tratament activ")}</div>
            <div style={{display:"flex",gap:8}}>
              <button className="bg" style={{fontSize:12,padding:"7px 14px"}} onClick={()=>setModal({type:"exportTratament"})}>{t("📥 Export")}</button>
              {!readOnly&&<button className="bp" style={{fontSize:12,padding:"7px 14px"}} onClick={()=>setModal({type:"addMed"})}>+ {t("Medicament")}</button>}
            </div>
          </div>
          <DraggableMedList meds={meds.filter(m=>m.active)} setMeds={setMeds} setModal={setModal} onDeleteMed={onDeleteMed} onToggleActive={onToggleActive} readOnly={readOnly}/>
          {meds.filter(m=>!m.active).length>0&&(<div>
            <div className="sec" style={{marginTop:12}}>{t("Inactive")}</div>
            {meds.filter(m=>!m.active).map(med=>(
              <MedCard key={med.id} med={med} setMeds={setMeds} setModal={setModal} onDeleteMed={onDeleteMed} onToggleActive={onToggleActive} inactive readOnly={readOnly}/>
            ))}
          </div>)}
        </div>
      )}

    </div>
  );
}




// ═══ FORMS ════════════════════════════════════════════════════════════════════
export function AddConditionForm({onAdd}) {
  const[text,setText]=useState("");
  // DatePicker foloseste DD.MM.YYYY
  const today=new Date();
  const[date,setDate]=useState(`${String(today.getDate()).padStart(2,"0")}.${String(today.getMonth()+1).padStart(2,"0")}.${today.getFullYear()}`);
  // Converteste DD.MM.YYYY -> YYYY-MM-DD pentru salvare
  function toISO(d){
    const p=d.split(".");
    if(p.length!==3) return d;
    return `${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`;
  }
  return (
    <div>
      <div style={{marginBottom:12}}><div className="lbl">{t("Diagnostic")}</div><input value={text} onChange={e=>setText(e.target.value)} placeholder={t("ex: Fibrilație atrială")}/></div>
      <div style={{marginBottom:20}}><div className="lbl">{t("Data diagnosticului")}</div><DatePicker value={date} onChange={setDate}/></div>
      <button className="bp" style={{width:"100%"}} onClick={()=>{if(text.trim())onAdd({text:text.trim(),date:toISO(date)});}}>{t("Adaugă")}</button>
    </div>
  );
}

export function AddDoctorForm({initial,onAdd,checkRole}) {
  const defaultSharing = {active:false,fisa:true,medici:true,documente:false,analize:true,tratament:true,vaccinuri:false,familie:false,sumarAI:false};
  const[d,setD]=useState(initial ? {...initial, sharing: initial.sharing||defaultSharing} : {spec:"Medicină de familie",name:"",hospital:"",address:"",phones:[""],email:"",notes:"",sharing:defaultSharing});

  function addPhone(){ setD(x=>({...x,phones:[...(x.phones||[""]),""]})); }
  function updatePhone(i,val){ setD(x=>({...x,phones:x.phones.map((p,j)=>j===i?val:p)})); }
  function removePhone(i){ setD(x=>({...x,phones:x.phones.filter((_,j)=>j!==i)})); }
  function toggleShare(key){ setD(x=>({...x,sharing:{...x.sharing,[key]:!x.sharing[key]}})); }
  async function toggleShareActive(){
    // La activare, verifică dacă emailul e deja îngrijitor
    if(!d.sharing?.active && checkRole && d.email){
      const existingRole = await checkRole(d.email);
      if(existingRole === 'caregiver'){
        fmAlert(`${t("Adresa")} ${d.email} ${t("are deja rol de îngrijitor pentru acest profil. Nu poate fi și medic. Revocă întâi rolul de îngrijitor.")}`);
        return;
      }
    }
    toggleShare("active");
  }

  const shareOptions = [
    {key:"fisa",label:t("Fișă personală")},
    {key:"medici",label:t("Medici")},
    {key:"documente",label:t("Documente")},
    {key:"analize",label:t("Analize")},
    {key:"tratament",label:t("Tratament")},
    {key:"vaccinuri",label:t("Vaccinuri")},
    {key:"sumarAI",label:t("Sumar AI")},
  ];
  // Familie nu se partajează niciodată cu medicul, indiferent de setările din formular
  React.useEffect(()=>{ if(d.sharing?.familie) setD(x=>({...x,sharing:{...x.sharing,familie:false}})); }, [d.sharing?.familie]);

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><div className="lbl">{t("Specializare")}</div>
          <SearchableSelect options={SPECS} value={d.spec} onChange={v=>setD(x=>({...x,spec:v}))} placeholder={t("Caută specializare...")}/>
        </div>
        <div><div className="lbl">{t("Nume medic")}</div><input value={d.name} onChange={e=>setD(x=>({...x,name:e.target.value}))} placeholder={t("Dr. Prenume Nume")}/></div>
        <div><div className="lbl">{t("Spital / Clinică")}</div><input value={d.hospital} onChange={e=>setD(x=>({...x,hospital:e.target.value}))}/></div>
        <div><div className="lbl">{t("Parafă")}</div><input value={d.parafa||""} onChange={e=>setD(x=>({...x,parafa:e.target.value}))} placeholder={t("ex: 900041")}/></div>
        <div><div className="lbl">{t("Email")}</div><input type="email" value={d.email} onChange={e=>{const v=e.target.value; setD(x=>({...x,email:v,sharing:{...x.sharing,active:v.trim()?x.sharing?.active:false}}));}} placeholder={t("medic@email.com")}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Adresă")}</div><input value={d.address||""} onChange={e=>setD(x=>({...x,address:e.target.value}))}/></div>
      </div>

      <div style={{marginBottom:12}}>
        <div className="lbl" style={{marginBottom:6}}>{t("Telefon")}</div>
        {(d.phones||[""]).map((ph,i)=>(
          <div key={i} style={{marginBottom:6}}>
            <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
              <div style={{flex:1}}><PhoneInput value={ph} onChange={v=>updatePhone(i,v)} placeholder={`${t("Telefon")} ${i+1}`}/></div>
              {(d.phones||[]).length>1&&<button className="bdel" style={{marginTop:8}} onClick={()=>confirmDelete(undefined,()=>removePhone(i))}><IconTrash/></button>}
            </div>
          </div>
        ))}
        <button className="bg" style={{fontSize:12,padding:"5px 12px",borderStyle:"dashed"}} onClick={addPhone}>{t("+ Adaugă telefon")}</button>
      </div>

      <div style={{marginBottom:12}}><div className="lbl">{t("Notițe")}</div><input value={d.notes} onChange={e=>setD(x=>({...x,notes:e.target.value}))} placeholder={t("ex: Control la 6 luni")}/></div>

      {/* Partajare date */}
      <div style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:10,padding:14,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:d.sharing?.active?12:0}}>
          <div>
            <div style={{fontSize:13,fontWeight:600}}>{t("📤 Partajare date cu medicul")}</div>
            <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>
              {d.email?`${t("Se trimite pe")} ${d.email}`:t("Adaugă email pentru a activa")}
            </div>
          </div>
          <label className="toggle">
            <input type="checkbox" checked={d.sharing?.active||false} onChange={toggleShareActive} disabled={!d.email}/>
            <span className="slider"/>
          </label>
        </div>
        {d.sharing?.active&&(
          <div>
            <div style={{fontSize:11,color:"var(--text-4)",marginBottom:8}}>{t("Selectează ce date partajezi:")}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {shareOptions.map(({key,label})=>(
                <div key={key} onClick={()=>toggleShare(key)} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:7,cursor:"pointer",background:d.sharing[key]?"var(--blue-bg)":"none",border:`1px solid ${d.sharing[key]?"#7c3aed":"var(--border-2)"}`,transition:"all .15s"}}>
                  <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${d.sharing[key]?"var(--blue)":"var(--border-2)"}`,background:d.sharing[key]?"var(--blue)":"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {d.sharing[key]&&<span style={{color:"#fff",fontSize:10,lineHeight:1}}>✓</span>}
                  </div>
                  <span style={{fontSize:12,color:d.sharing[key]?"var(--blue-2)":"var(--text-2)"}}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button className="bp" style={{width:"100%"}} onClick={()=>{if(d.name.trim())onAdd(d);}}>{t("Salvează")}</button>
    </div>
  );
}

export function AddEventForm({initial, onAdd, doctors=[], events={}}) {
  const[ev,setEv]=useState({
    spec:"Cardiologie", title:"Cardiologie",
    date:initial?.date||todayISO(),
    time:"09:00", dr:"", hospital:"", address:"", phone:"", visitNote:"", reminderHours:24
  });

  function onSpecChange(spec) {
    // 1. Caută medici înregistrați pe această specializare
    const docsBySpec = (doctors||[]).filter(d=>d.spec===spec);
    
    // 2. Caută ultima programare pe această specializare din istoric
    const allEvents = Object.values(events).flat();
    const lastEv = allEvents
      .filter(e=>e.spec===spec && e.dr)
      .sort((a,b)=>(b.date||"").localeCompare(a.date||""))[0];

    let dr="", hospital="", address="", phone="";

    if(docsBySpec.length===1) {
      // Un singur medic înregistrat pe specializare
      const d = docsBySpec[0];
      dr=d.name||""; hospital=d.hospital||""; address=d.address||"";
      phone=(d.phones||[])[0]||d.phone||"";
    } else if(docsBySpec.length>1) {
      // Mai mulți medici — ia din ultima programare
      const bestDoc = lastEv ? docsBySpec.find(d=>d.name===lastEv.dr)||docsBySpec[0] : docsBySpec[0];
      dr=bestDoc.name||""; hospital=bestDoc.hospital||""; address=bestDoc.address||"";
      phone=(bestDoc.phones||[])[0]||bestDoc.phone||"";
    } else if(lastEv) {
      // Niciun medic înregistrat — ia din ultima programare pe această specializare
      dr=lastEv.dr||""; hospital=lastEv.hospital||""; address=lastEv.address||"";
      phone=lastEv.phone||"";
    }

    setEv(x=>({...x, spec, title:spec, dr, hospital, address, phone}));
  }

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><div className="lbl">{t("Specializare")}</div>
          <SearchableSelect options={SPECS} value={ev.spec} onChange={v=>onSpecChange(v)} placeholder={t("Caută specializare...")}/>
        </div>
        <div><div className="lbl">{t("Data")}</div>
          <DatePicker
            value={ev.date ? fmtD(ev.date) : ""}
            onChange={v=>{
              // DatePicker dă DD.MM.YYYY; convertim la ISO YYYY-MM-DD pentru salvare
              const parts=(v||"").split(".");
              const iso = parts.length===3 ? `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}` : v;
              setEv(x=>({...x,date:iso}));
            }}/>
        </div>
        <div><div className="lbl">{t("Ora")}</div><TimeInput value={ev.time} onChange={v=>setEv(x=>({...x,time:v}))}/></div>
        <div><div className="lbl">{t("Medic")}</div><input value={ev.dr} onChange={e=>setEv(x=>({...x,dr:e.target.value}))} placeholder={t("Dr. Prenume Nume")}/></div>
        <div><div className="lbl">{t("Spital / Clinică")}</div><input value={ev.hospital} onChange={e=>setEv(x=>({...x,hospital:e.target.value}))}/></div>
        <div><div className="lbl">{t("Telefon")}</div><input value={ev.phone} onChange={e=>setEv(x=>({...x,phone:e.target.value}))}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Adresă")}</div><input value={ev.address} onChange={e=>setEv(x=>({...x,address:e.target.value}))}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Notițe vizită")}</div><textarea value={ev.visitNote} onChange={e=>setEv(x=>({...x,visitNote:e.target.value}))} rows={2} placeholder={t("Observații, recomandări...")}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Reminder")}</div>
          <Dropdown value={ev.reminderHours} onChange={v=>setEv(x=>({...x,reminderHours:+v}))} options={REMINDER_OPTS}/>
        </div>
      </div>
      <button className="bg" style={{width:"100%",marginBottom:8}} onClick={()=>addAppointmentToCalendar(ev, ev.reminderHours)}>{t("📅 Adaugă în calendarul telefonului")}</button>
      <button className="bp" style={{width:"100%"}} onClick={()=>{if(ev.spec)onAdd(ev);}}>{t("Salvează programare")}</button>
    </div>
  );
}
const REMINDER_OPTS = [
  {v:0,l:"Fără reminder"},
  {v:1,l:"Cu 1 oră înainte"},
  {v:2,l:"Cu 2 ore înainte"},
  {v:3,l:"Cu 3 ore înainte"},
  {v:24,l:"Cu o zi înainte"},
  {v:48,l:"Cu 2 zile înainte"},
  {v:168,l:"Cu o săptămână înainte"},
];

export function EditEventForm({ev,onSave,onCancel}) {
  const[e,setE]=useState(ev);
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
        <div><div className="lbl">{t("Specializare")}</div><SearchableSelect options={SPECS} value={e.spec||""} onChange={v=>setE(x=>({...x,spec:v,title:v}))} placeholder={t("Caută specializare...")}/></div>
        <div><div className="lbl">{t("Data")}</div>
          <DatePicker
            value={e.date ? fmtD(e.date) : ""}
            onChange={v=>{
              const parts=(v||"").split(".");
              const iso = parts.length===3 ? `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}` : v;
              setE(x=>({...x,date:iso}));
            }}/>
        </div>
        <div><div className="lbl">{t("Ora")}</div><TimeInput value={e.time||""} onChange={v=>setE(x=>({...x,time:v}))}/></div>
        <div><div className="lbl">{t("Medic")}</div><input value={e.dr||""} onChange={x=>setE(v=>({...v,dr:x.target.value}))}/></div>
        <div><div className="lbl">{t("Spital")}</div><input value={e.hospital||""} onChange={x=>setE(v=>({...v,hospital:x.target.value}))}/></div>
        <div><div className="lbl">{t("Telefon")}</div><input value={e.phone||""} onChange={x=>setE(v=>({...v,phone:x.target.value}))}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Adresă")}</div><input value={e.address||""} onChange={x=>setE(v=>({...v,address:x.target.value}))}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Notițe")}</div><textarea value={e.visitNote||""} onChange={x=>setE(v=>({...v,visitNote:x.target.value}))} rows={2}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Reminder")}</div>
          <Dropdown value={e.reminderHours??24} onChange={v=>setE(x=>({...x,reminderHours:+v}))} options={REMINDER_OPTS}/>
        </div>
      </div>
      <button className="bg" style={{width:"100%",marginBottom:8}} onClick={()=>addAppointmentToCalendar(e, e.reminderHours??24)}>{t("📅 Adaugă în calendarul telefonului")}</button>
      <div style={{display:"flex",gap:8}}>
        <button className="bp" style={{flex:1}} onClick={()=>onSave(e)}>{t("Salvează")}</button>
        <button className="bg" onClick={onCancel}>{t("Anulează")}</button>
      </div>
    </div>
  );
}

export function EventRow({ev, et, onEdit, onDelete, family, readOnly=false}) {
  const [expanded, setExpanded] = React.useState(false);
  const [noteText, setNoteText] = React.useState(ev.visitNote||"");

  if(ev.isNote) {
    // Notițe — click expandează cu câmp editabil + coș
    return (
      <div style={{borderRadius:10,overflow:"hidden",marginBottom:6,border:`1px solid ${et.color}33`}}>
        <div onClick={()=>!readOnly&&setExpanded(v=>!v)} style={{padding:"10px 14px",background:et.bg,cursor:readOnly?"default":"pointer",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14}}>📝</span>
          <div style={{flex:1,fontSize:13,color:"var(--text-2)",fontStyle:"italic",whiteSpace:"pre-wrap"}}>{ev.visitNote||t("Notiță goală")}</div>
          <CaregiverBadge addedBy={ev.addedBy} addedByName={ev.addedByName} addedByEmail={ev.addedByEmail} family={family} size={16}/>
        </div>
        {!readOnly&&expanded&&(
          <div style={{padding:"10px 12px",background:"var(--bg)",display:"flex",gap:8,alignItems:"flex-start"}}>
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
              onBlur={()=>onEdit(noteText)}
              rows={3} style={{flex:1,background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,
                color:"var(--text)",padding:"8px 10px",fontSize:13,fontFamily:"inherit",resize:"none"}}/>
            <button className="bdel" style={{padding:"6px 10px",fontSize:14,flexShrink:0}} onClick={()=>{onDelete();setExpanded(false);}}><IconTrash/></button>
          </div>
        )}
      </div>
    );
  }

  // Evenimente normale — click expandează cu Editează + coș
  return (
    <div style={{borderRadius:10,overflow:"hidden",marginBottom:6,border:`1px solid ${et.color}33`}}>
      <div onClick={()=>!readOnly&&setExpanded(v=>!v)} style={{padding:"10px 14px",background:et.bg,cursor:readOnly?"default":"pointer"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
          <span className="pill" style={{background:"transparent",color:et.color,border:`1px solid ${et.color}66`,fontSize:10}}>{ev.spec||"Consult"}</span>
          {ev.time&&<span style={{fontSize:11,color:"var(--text-2)",fontFamily:"monospace"}}>{ev.time}</span>}
          <span style={{marginLeft:"auto"}}><CaregiverBadge addedBy={ev.addedBy} addedByName={ev.addedByName} addedByEmail={ev.addedByEmail} family={family} size={16}/></span>
        </div>
        {ev.title && ev.title!==ev.spec && <div style={{fontWeight:600,fontSize:14}}>{ev.title}</div>}
        {ev.dr&&<div style={{fontSize:12,color:"var(--text-2)",marginTop:2}}>{ev.dr}{ev.hospital?" · "+ev.hospital:""}</div>}
        {ev.visitNote&&<div style={{fontSize:12,color:"var(--text-3)",marginTop:4,fontStyle:"italic",whiteSpace:"pre-wrap"}}>📝 {ev.visitNote}</div>}
      </div>
      {!readOnly&&expanded&&(
        <div style={{padding:"8px 12px",background:"var(--bg)",display:"flex",gap:8}}>
          <button className="bg" style={{flex:1,fontSize:13,padding:"7px"}} onClick={()=>{onEdit();setExpanded(false);}}>{t("✎ Editează")}</button>
          <button className="bdel" style={{fontSize:13,padding:"7px 12px"}} onClick={()=>{onDelete();setExpanded(false);}}><IconTrash/></button>
        </div>
      )}
    </div>
  );
}

export function SymptomRow({symptom, onEdit, onDelete, family, readOnly=false}) {
  return (
    <div style={{marginBottom:6,borderRadius:10,overflow:"hidden",border:"1px solid var(--purple-line)"}}>
      <div onClick={readOnly?undefined:onEdit} style={{padding:"9px 12px",background:"var(--purple-bg-soft)",cursor:readOnly?"default":"pointer",display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:18}}>🤒</span>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--purple-2)"}}>{symptom.items?.join(", ")}</div>
            {symptom.time&&<span style={{fontSize:11,color:"var(--text-3)",fontFamily:"monospace"}}>{symptom.time}</span>}
          </div>
          {symptom.note&&<div style={{fontSize:11,color:"var(--text-2)",marginTop:2}}>{symptom.note}</div>}
        </div>
        <CaregiverBadge addedBy={symptom.addedBy} addedByName={symptom.addedByName} addedByEmail={symptom.addedByEmail} family={family} size={16}/>
      </div>
    </div>
  );
}

export function AddNoteForm({selDay, onAdd}) {
  const[text,setText,clearDraft]=useDraftState("note_"+selDay, "");
  return (
    <div>
      <div className="lbl" style={{marginBottom:8}}>Notiță pentru {fmtD(selDay)}</div>
      <textarea value={text} onChange={e=>setText(e.target.value)} rows={4}
        placeholder={t("Scrie notiță...")}
        style={{width:"100%",background:"var(--bg)",border:"1px solid var(--border-2)",borderRadius:8,
          color:"var(--text)",padding:"10px 12px",fontSize:14,fontFamily:"inherit",resize:"none"}}
        autoFocus/>
      <button className="bp" style={{width:"100%",marginTop:12}} disabled={!text.trim()}
        onClick={()=>{ const t=text.trim(); clearDraft(); onAdd(t); }}>{t("Salvează")}</button>
    </div>
  );
}

export function AddSymptomForm({onAdd, initial, customSymptoms=[], onAddCustomSymptom, onDeleteCustomSymptom}) {
  const nowTime = new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false});
  const[sel,setSel]=useState(initial?.items||[]);
  const[symQ,setSymQ]=useState("");
  const[note,setNote,clearNoteDraft]=useDraftState(initial?._dbId?("symnote_edit_"+initial._dbId):"symnote_new", initial?.note||"");
  const[date,setDate]=useState(initial?.date||todayISO());
  const[time,setTime]=useState(initial?.time!=null ? initial.time : nowTime);
  function toggleSel(s){ setSel(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s]); }
  // lista completă = lista fixă + simptomele custom salvate (fără duplicate)
  const fullList = [...SYMPTOMS_LIST, ...customSymptoms.filter(s=>!SYMPTOMS_LIST.includes(s))];
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <div className="lbl">{t("Data")}</div>
          <DateField value={date} onChange={v=>setDate(v)}/>
        </div>
        <div>
          <div className="lbl">{t("Ora")}</div>
          <TimeInput value={time} onChange={setTime}/>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <div className="lbl" style={{marginBottom:8}}>{t("Selectează simptome")} {sel.length>0&&<span style={{color:"var(--blue)"}}>({sel.length} {t("selectate")})</span>}</div>
        <div style={{display:"flex",gap:6,marginBottom:8}}>
          <input value={symQ} onChange={e=>setSymQ(e.target.value)} placeholder={t("Caută sau scrie un simptom...")} style={{flex:1,boxSizing:"border-box"}}/>
          {symQ.trim() && !fullList.some(s=>t(s).toLowerCase()===symQ.trim().toLowerCase()) && (
            <button className="bp" style={{fontSize:12,padding:"0 14px",whiteSpace:"nowrap"}} onClick={()=>{ const v=symQ.trim(); if(!sel.includes(v))setSel(p=>[...p,v]); if(onAddCustomSymptom)onAddCustomSymptom(v); setSymQ(""); }}>{t("+ Adaugă")}</button>
          )}
        </div>
        <div style={{maxHeight:280,overflowY:"auto",border:"1px solid var(--border-2)",borderRadius:8,padding:4}}>
          {[...fullList].sort((a,b)=>{const aE=/^alt(a|ul)$/i.test(a),bE=/^alt(a|ul)$/i.test(b);if(aE&&!bE)return 1;if(bE&&!aE)return -1;return t(a).localeCompare(t(b));}).filter(s=>{const q=symQ.trim().toLowerCase();return !q||t(s).toLowerCase().includes(q);}).map(s=>{
            const isCustom = customSymptoms.includes(s) && !SYMPTOMS_LIST.includes(s);
            return (
            <div key={s} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:6,background:sel.includes(s)?"var(--blue-bg)":"none",transition:"background .15s",marginBottom:1}}>
              <div onClick={()=>toggleSel(s)} style={{flex:1,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${sel.includes(s)?"var(--blue)":"var(--border-2)"}`,background:sel.includes(s)?"var(--blue)":"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                  {sel.includes(s)&&<span style={{color:"#fff",fontSize:11,fontWeight:700,lineHeight:1}}>✓</span>}
                </div>
                <span style={{fontSize:13,color:sel.includes(s)?"var(--blue-2)":"var(--text)"}}>{t(s)}</span>
              </div>
              {isCustom && onDeleteCustomSymptom && (
                <button onClick={(e)=>{e.stopPropagation(); onDeleteCustomSymptom(s); if(sel.includes(s))toggleSel(s);}} title={t("Șterge din listă")}
                  style={{background:"none",border:"none",color:"#f0a0a0",cursor:"pointer",padding:"0 4px",flexShrink:0,display:"flex",alignItems:"center"}}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                </button>
              )}
            </div>
          )})}
        </div>
        {sel.length>0&&(
          <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:4}}>
            {sel.map((s,i)=>(
              <span key={i} className="pill" style={{background:"var(--blue-bg)",color:"var(--blue-2)",cursor:"pointer"}} onClick={()=>toggleSel(s)}>
                {t(s)} <span style={{marginLeft:4,color:"var(--red)",fontSize:10}}>✕</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={{marginBottom:20}}><div className="lbl">{t("Notă suplimentară")}</div><textarea value={note} onChange={e=>setNote(e.target.value)} rows={2} placeholder={t("Intensitate, durată, context...")}/></div>
      <button className="bp" style={{width:"100%"}} disabled={!sel.length && !initial?._dbId} onClick={()=>{clearNoteDraft();onAdd({date,time,items:sel,note});}}>{sel.length?t("Salvează simptome"):t("Șterge intrarea")}</button>
    </div>
  );
}

export function DeleteAccountForm({email, onConfirm, onCancel}) {
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <div style={{padding:"12px 14px",background:"var(--red-bg-soft)",border:"1px solid var(--red-line)",borderRadius:8,marginBottom:16}}>
        <div style={{fontSize:14,fontWeight:600,color:"var(--red-2)",marginBottom:6}}>{t("Această acțiune este definitivă")}</div>
        <div style={{fontSize:13,color:"var(--text)",lineHeight:1.6}}>
          Se vor șterge permanent <strong>{t("toate")}</strong> datele contului <strong>{email}</strong>:
          toate profilurile, documentele, analizele, tratamentele, vaccinurile, istoricul familial și invitațiile.
          <br/><br/>
          Datele <strong>{t("nu pot fi recuperate")}</strong>.
        </div>
      </div>
      <div style={{display:"flex",gap:10}}>
        <button className="bg" style={{flex:1}} onClick={onCancel} disabled={busy}>{t("Anulează")}</button>
        <button
          style={{flex:1,padding:"9px 18px",borderRadius:8,border:"none",fontFamily:"inherit",fontSize:13,fontWeight:600,
            background:"#b91c1c",color:"#fff",cursor:busy?"not-allowed":"pointer"}}
          disabled={busy}
          onClick={async()=>{ setBusy(true); await onConfirm(); setBusy(false); }}>
          {busy?t("Se șterge..."):t("Șterge definitiv")}
        </button>
      </div>
    </div>
  );
}
export function AddProfileForm({onAdd}) {
  const[name,setName]=useState("");
  const[relation,setRelation]=useState("Tată");
  const[dob,setDob]=useState("");
  return (
    <div>
      <div style={{marginBottom:12}}><div className="lbl">{t("Nume")}</div><input value={name} onChange={e=>setName(e.target.value)} placeholder={t("ex: Gheorghe Ionescu")} autoFocus/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div><div className="lbl">{t("Relație")}</div>
          <Dropdown value={relation} onChange={v=>setRelation(v)} options={["Eu","Soț","Soție","Tată","Mamă","Fiu","Fiică","Bunic","Bunică","Frate","Soră","Altul"]}/>
        </div>
        <div><div className="lbl">{t("Data nașterii")}</div><DatePicker value={dob} onChange={setDob}/></div>
      </div>
      <div style={{padding:"10px 14px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",marginBottom:16,fontSize:12,color:"var(--text-3)",lineHeight:1.6}}>
        💡 Un profil nou creează un dosar medical separat — Acasă, Documente, Analize și Tratament complet separate pentru această persoană.
      </div>
      <button className="bp" style={{width:"100%"}} disabled={!name.trim()} onClick={()=>onAdd(name,relation,dob)}>{t("Creează profil")}</button>
    </div>
  );
}

function calcStockDuration(med) {
  // Calculează câte unități se consumă pe zi
  if(!med.active || med.scheduleType==="ondemand") return null;
  const stock = parseFloat(med.stock)||0;
  if(stock <= 0) return {days:0, date:null};

  let dosePerDay = 0;

  if(med.scheduleType==="interval_hours") {
    const intervalH = parseFloat(med.intervalHours)||8;
    const dosePerIntake = parseFloat(med.schedule?.[0]?.dose)||1;
    dosePerDay = (24 / intervalH) * dosePerIntake;
  } else if(med.scheduleType==="specific_days") {
    const days = med.weekDays?.length || 1;
    const dosePerIntake = (med.schedule||[]).reduce((s,x)=>s+(parseFloat(x.dose)||1),0);
    dosePerDay = (days / 7) * dosePerIntake;
  } else if(med.scheduleType==="cycle") {
    const onDays = parseFloat(med.cyclicOn)||21;
    const offDays = parseFloat(med.cyclicOff)||7;
    const dosePerIntake = (med.schedule||[]).reduce((s,x)=>s+(parseFloat(x.dose)||1),0);
    dosePerDay = (onDays / (onDays + offDays)) * dosePerIntake;
  } else {
    // daily - suma dozelor din schedule
    dosePerDay = (med.schedule||[]).reduce((s,x)=>s+(parseFloat(x.dose)||1),0);
  }

  if(dosePerDay <= 0) return null;

  const days = Math.floor(stock / dosePerDay);
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + days);

  return {
    days,
    date: endDate.toLocaleDateString("ro-RO",{day:"2-digit",month:"2-digit",year:"numeric"})
  };
}

export function DraggableMedList({meds, setMeds, setModal, onDeleteMed, onToggleActive, readOnly=false}) {
  const sorted = [...meds].sort((a,b)=>(a.order||0)-(b.order||0));
  const [dragId, setDragId] = React.useState(null);
  const [overId, setOverId] = React.useState(null);
  const scrollTimer = React.useRef(null);
  const dragIdRef = React.useRef(null);
  const overIdRef = React.useRef(null);
  const orderRef = React.useRef([]);
  orderRef.current = sorted.map(m=>m.id);

  function stopAutoScroll(){ if(scrollTimer.current){ clearInterval(scrollTimer.current); scrollTimer.current=null; } }
  function startAutoScroll(dir){
    if(scrollTimer.current) return;
    scrollTimer.current = setInterval(()=>window.scrollBy(0, dir*10), 16);
  }

  function cardUnderPoint(clientY){
    const els = document.querySelectorAll('[data-medrow]');
    for(const el of els){
      const r = el.getBoundingClientRect();
      if(clientY >= r.top && clientY <= r.bottom) return el.getAttribute('data-medrow');
    }
    return null;
  }

  function processY(clientY){
    const margin = 90;
    if(clientY < margin) startAutoScroll(-1);
    else if(clientY > window.innerHeight - margin) startAutoScroll(1);
    else stopAutoScroll();
    const over = cardUnderPoint(clientY);
    if(over && over !== overIdRef.current){ overIdRef.current = over; setOverId(over); }
  }

  // Referințe STABILE pentru handlere (altfel removeEventListener nu le scoate)
  const handlersRef = React.useRef(null);
  if(!handlersRef.current){
    handlersRef.current = {
      move: (e)=>{ if(!dragIdRef.current) return; e.preventDefault(); processY(e.touches[0].clientY); },
      mouseMove: (e)=>{ if(!dragIdRef.current) return; processY(e.clientY); },
      end: ()=>{
        stopAutoScroll();
        const dId = String(dragIdRef.current), oId = String(overIdRef.current);
        if(dId && oId && dId !== oId && overIdRef.current!=null){
          const ids = orderRef.current.map(x=>String(x));
          const from = ids.indexOf(dId), to = ids.indexOf(oId);
          if(from !== -1 && to !== -1){
            ids.splice(from,1); ids.splice(to,0,dId);
            // ids = ordinea nouă (string-uri). Aplicăm order după poziția în ids.
            setMeds(ms=>ms.map(m=>({...m, order: ids.indexOf(String(m.id))})));
          }
        }
        dragIdRef.current=null; overIdRef.current=null;
        setDragId(null); setOverId(null);
        const h = handlersRef.current;
        document.removeEventListener('touchmove', h.move);
        document.removeEventListener('touchend', h.end);
        document.removeEventListener('mousemove', h.mouseMove);
        document.removeEventListener('mouseup', h.end);
      }
    };
  }

  function beginDrag(id){
    dragIdRef.current = id;
    setDragId(id);
    const h = handlersRef.current;
    document.addEventListener('touchmove', h.move, { passive:false });
    document.addEventListener('touchend', h.end);
    document.addEventListener('mousemove', h.mouseMove);
    document.addEventListener('mouseup', h.end);
  }

  return (
    <div>
      {sorted.map(med=>{
        const isDragged = String(dragId)===String(med.id);
        const isOver = String(overId)===String(med.id) && !isDragged;
        return (
        <div key={med.id} data-medrow={med.id}
          style={{
            opacity: isDragged ? 0.4 : 1,
            borderTop: isOver ? "3px solid var(--blue)" : "3px solid transparent",
            boxShadow: isOver ? "0 -2px 8px rgba(96,165,250,.4)" : "none",
            transition: "border-color 0.12s, box-shadow 0.12s",
            display:"flex", alignItems:"stretch"
          }}>
          {/* Mâner în stânga */}
          {!readOnly&&<div
            onTouchStart={()=>beginDrag(med.id)}
            onMouseDown={()=>beginDrag(med.id)}
            style={{display:"flex",alignItems:"center",justifyContent:"center",width:22,marginBottom:8,
              cursor:"grab",color:isDragged?"var(--blue)":"#3a4452",touchAction:"none",userSelect:"none",
              fontSize:18,flexShrink:0,lineHeight:1}}
            title="Trage pentru a muta">
            ⋮
          </div>}
          <div style={{flex:1,minWidth:0}}>
            <MedCard med={med} setMeds={setMeds} setModal={setModal} onDeleteMed={onDeleteMed} onToggleActive={onToggleActive} isDragging={String(dragId)===String(med.id)} readOnly={readOnly}/>
          </div>
        </div>
      );})}
      {dragId&&<div style={{textAlign:"center",fontSize:11,color:"var(--blue)",padding:"6px 0"}}>{t("Trage în sus sau în jos · ridică degetul pentru a fixa")}</div>}
    </div>
  );
}

export function MedCard({med, setMeds, setModal, onDeleteMed, onToggleActive, inactive=false, isDragging=false, readOnly=false}) {
  const duration = calcStockDuration(med);
  const isLow = med.stock <= med.alertAt;

  return (
    <div style={{marginBottom:8,borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",borderLeft:`3px solid ${med.color}`,opacity:inactive?0.55:1}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",background:"var(--surface)"}}>
        <div onClick={readOnly?undefined:()=>setModal({type:"editMed",data:med})} style={{flex:1,minWidth:0,cursor:readOnly?"default":"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontWeight:700,fontSize:15}}>{med.name}</span>
            <span style={{fontSize:14}}>{getFormEmoji(med.form)}</span>
            {med.startDate&&<span style={{fontSize:11,color:"var(--text-4)",fontFamily:"monospace"}}>{t("din")} {fmtD(med.startDate)}</span>}
          </div>
          {med.indication&&<div style={{fontSize:11,color:"var(--text-3)",fontStyle:"italic",marginTop:2}}>{med.indication}</div>}
          <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
            {med.scheduleType==="ondemand"
              ?<span style={{fontSize:11,color:"var(--text-3)"}}>{t("La nevoie")}</span>
              :(med.schedule||[]).map((s,i)=><span key={i} style={{fontSize:11,color:"var(--blue)",fontFamily:"monospace",background:"var(--bg)",padding:"1px 7px",borderRadius:5}}>{s.time} ×{s.dose||1}</span>)
            }
          </div>
          {duration&&!inactive&&duration.days>0&&(!readOnly||isLow)&&(
            <div style={{marginTop:4,fontSize:11,color:duration.days<=5?"var(--amber-2)":"var(--text-3)"}}>
              {duration.days<=5?"⚠ ":""}{t("Stoc suficient până la")} {duration.date}
            </div>
          )}
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {(!readOnly||isLow)&&<span style={{fontSize:12,color:isLow?"var(--red)":"var(--text-3)"}}>{isLow?"⚠ ":""}{med.stock}</span>}
            {!readOnly&&<label className="toggle" onClick={e=>e.stopPropagation()}>
              <input type="checkbox" checked={med.active} onChange={()=>{
                if(onToggleActive) onToggleActive(med);
                else setMeds(ms=>ms.map(m=>m.id===med.id?{...m,active:!m.active}:m));
              }}/>
              <span className="slider"/>
            </label>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AddMedDoseForm({meds, medLogs, onAdd, defaultDay}) {
  const [selMed, setSelMed] = useState(null);
  const [time, setTime] = useState(new Date().toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",hour12:false}));
  const [dose, setDose] = useState("1");
  const [day, setDay] = useState(defaultDay || todayISO());
  const [search, setSearch] = useState("");

  // Calculează frecvența de folosire (câte administrări confirmate are fiecare medicament)
  const usageCount = React.useMemo(()=>{
    const counts = {};
    Object.values(medLogs||{}).forEach(dayLog=>{
      Object.entries(dayLog||{}).forEach(([medId, doses])=>{
        const done = (doses||[]).filter(d=>d.status==="done").length;
        counts[medId] = (counts[medId]||0) + done;
      });
    });
    return counts;
  }, [medLogs]);

  const filtered = meds.filter(m=>m.active&&(
    !search || m.name.toLowerCase().includes(search.toLowerCase())
  )).sort((a,b)=>(usageCount[b.id]||0)-(usageCount[a.id]||0)); // cele mai folosite primele

  if(!selMed) return (
    <div>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t("Caută medicament...")} style={{marginBottom:10,width:"100%"}} autoFocus/>
      <div style={{maxHeight:320,overflowY:"auto"}}>
        {filtered.length===0&&<div style={{color:"var(--text-4)",fontSize:13,textAlign:"center",padding:20}}>{t("Niciun medicament activ în plan.")}</div>}
        {filtered.map(med=>(
          <button key={med.id} onClick={()=>setSelMed(med)} style={{display:"flex",alignItems:"center",gap:12,width:"100%",background:"none",border:"1px solid var(--border)",borderRadius:10,color:"var(--text)",padding:"10px 14px",cursor:"pointer",marginBottom:6,fontFamily:"inherit",textAlign:"left"}}
            onMouseEnter={e=>e.currentTarget.style.background="var(--border)"}
            onMouseLeave={e=>e.currentTarget.style.background="none"}>
            <div style={{width:10,height:10,borderRadius:"50%",background:med.color,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:14}}>{med.name}</div>
              <div style={{fontSize:11,color:"var(--text-3)"}}>{t(med.form)} · {t("Stoc:")} {med.stock}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"10px 14px",background:"var(--bg)",borderRadius:10,border:`1px solid ${selMed.color}44`}}>
        <div style={{width:10,height:10,borderRadius:"50%",background:selMed.color}}/>
        <div style={{flex:1}}>
          <div style={{fontWeight:600}}>{selMed.name}</div>
          <div style={{fontSize:11,color:"var(--text-3)"}}>{t(selMed.form)} · {t("Stoc:")} {selMed.stock}</div>
        </div>
        <button onClick={()=>setSelMed(null)} style={{background:"none",border:"none",color:"var(--text-3)",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{t("← Schimbă")}</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <div>
          <div className="lbl">{t("Data")}</div>
          <DateField value={day} onChange={v=>setDay(v)} style={{width:"100%"}}/>
        </div>
        <div>
          <div className="lbl">{t("Ora administrare")}</div>
          <TimeInput value={time} onChange={setTime}/>
        </div>
        <div>
          <div className="lbl">{t("Cantitate")}</div>
          <input type="number" min="0.5" step="0.5" value={dose} onChange={e=>setDose(e.target.value)} style={{width:"100%",textAlign:"center"}}/>
        </div>
      </div>
      <button className="bp" style={{width:"100%",padding:"10px"}} onClick={()=>onAdd(selMed.id,time,dose,day)}>
        💊 {t("Adaugă doză")}
      </button>
    </div>
  );
}

export function AddMedForm({onAdd, initial, onDelete}) {
  const defaultSched = {scheduleType:"daily", schedule:[{time:"09:00",dose:"1"}], scheduleOnDemand:false, intervalHours:"8", intervalDays:"1", intervalMonths:"1", weekDays:[], cyclicOn:"21", cyclicOff:"7"};
  const[m,setMState]=useState(initial ? {
    ...initial,
    stock: String(initial.stock!=null ? initial.stock : 30),
    alertAt: String(initial.alertAt!=null ? initial.alertAt : 5),
    notifyStock: initial.notifyStock ?? true,
    notifyStockDays: String(initial.notifyStockDays!=null ? initial.notifyStockDays : 3),
    scheduleType: initial.scheduleType||"daily",
    schedule: initial.schedule||[{time:"09:00",dose:"1"}],
    weekDays: initial.weekDays||[],
    intervalHours: String(initial.intervalHours||8),
    intervalDays: String(initial.intervalDays||1),
    intervalMonths: String(initial.intervalMonths||1),
    cyclicOn: String(initial.cyclicOn||21),
    cyclicOff: String(initial.cyclicOff||7),
  } : {name:"",dci:"",form:"Comprimată",indication:"",stock:"30",alertAt:"5",notifyStock:true,notifyStockDays:"3",startDate:todayISO(),...defaultSched});
  const dirtyRef = React.useRef(false);
  const[suggestions,setSuggestions]=useState([]);
  const[showSugg,setShowSugg]=useState(false);

  // Intercept all m changes to set dirty flag
  function setM(updater) {
    setMState(updater);
    dirtyRef.current = true;
    window._formDirty = true;
  }

  function onNameChange(val) {
    setM(x=>({...x,name:val}));
    if(val.length>=2) {
      const s=MOCK_MEDS.filter(d=>d.name.toLowerCase().includes(val.toLowerCase())||d.dci.toLowerCase().includes(val.toLowerCase())).slice(0,8);
      setSuggestions(s); setShowSugg(s.length>0);
    } else { setSuggestions([]); setShowSugg(false); }
  }
  // Unele intrări din nomenclator nu au forma completată — nu suprascrie forma aleasă cu gol
  function pickSugg(s){ setM(x=>({...x,name:s.name,dci:s.dci,form:s.form||x.form})); setSuggestions([]); setShowSugg(false); }
  const DEFAULT_TIMES = ["09:00","15:00","21:00"];
  function addSched(){ const t=DEFAULT_TIMES[m.schedule.length]||"08:00"; setM(x=>({...x,schedule:[...x.schedule,{time:t,dose:"1"}].sort((a,b)=>(a.time||"").localeCompare(b.time||""))})); }
  function updSched(i,field,val){ setM(x=>{
    let schedule=x.schedule.map((s,j)=>j!==i?s:{...s,[field]:val});
    // Ordonează dozele crescător după oră când se schimbă o oră
    if(field==="time") schedule=[...schedule].sort((a,b)=>(a.time||"").localeCompare(b.time||""));
    return {...x,schedule};
  }); }
  function remSched(i){ setM(x=>({...x,schedule:x.schedule.filter((_,j)=>j!==i)})); }
  function toggleWeekDay(d){ setM(x=>({...x,weekDays:x.weekDays?.includes(d)?x.weekDays.filter(w=>w!==d):[...(x.weekDays||[]),d]})); }

  const SCHED_TYPES = [
    {k:"daily",l:"Zilnic"},
    {k:"ondemand",l:"La nevoie"},
    {k:"interval_hours",l:"Interval ore"},
    {k:"interval_days",l:"Interval zile"},
    {k:"interval_months",l:"Interval luni"},
    {k:"weekdays",l:"Zile săptămână"},
    {k:"cyclic",l:"Ciclic"},
  ];
  const WDAY_FULL = ["Lu","Ma","Mi","Jo","Vi","Sâ","Du"];

  return (
    <div>
      <div style={{marginBottom:12,position:"relative"}}>
        <div className="lbl">{t("Denumire comercială ")}<span style={{color:"var(--text-4)",fontWeight:400,textTransform:"none",letterSpacing:0}}>{t("(caută în nomenclatorul ANMDMR)")}</span></div>
        <input value={m.name} onChange={e=>onNameChange(e.target.value)}
          onFocus={()=>m.name.length>=2&&setShowSugg(suggestions.length>0)}
          onBlur={()=>setTimeout(()=>setShowSugg(false),500)}
          placeholder={t("ex: Metformin, Amlodipină...")} autoComplete="off"/>
        {showSugg&&suggestions.length>0&&(
          <div
            style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:8,zIndex:60,maxHeight:240,overflowY:"auto",marginTop:2,boxShadow:"0 8px 24px rgba(0,0,0,.5)",WebkitOverflowScrolling:"touch"}}>
            {suggestions.map((s,i)=>(
              <button key={i}
                onMouseDown={e=>e.preventDefault()}
                onClick={e=>{e.preventDefault();pickSugg(s);}}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",background:"none",border:"none",color:"var(--text)",padding:"12px 14px",cursor:"pointer",fontSize:13,fontFamily:"inherit",borderBottom:"1px solid var(--border)",textAlign:"left"}}
                onMouseEnter={e=>e.currentTarget.style.background="var(--border)"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}>
                <span style={{fontWeight:600}}>{s.name}</span>
                <span style={{color:"var(--text-3)",fontSize:11}}>{s.dci} · {s.form}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><div className="lbl">{t("DCI (substanță activă)")}</div><input value={m.dci} onChange={e=>setM(x=>({...x,dci:e.target.value}))} placeholder={t("se completează automat")}/></div>
        <div><div className="lbl">{t("Formă farmaceutică")}</div>
          <Dropdown value={m.form} onChange={v=>setM(x=>({...x,form:v}))} options={DOSE_FORMS}/>
        </div>
        <div><div className="lbl">{t("Data începerii")}</div><DateField value={m.startDate} onChange={v=>setM(x=>({...x,startDate:v}))}/></div>
        <div style={{gridColumn:"span 2"}}><div className="lbl">{t("Indicație / De ce se ia")}</div>
          <input value={m.indication} onChange={e=>setM(x=>({...x,indication:e.target.value}))} placeholder={t("ex: Control tensiune arterială, diabet...")}/>
        </div>
        <div><div className="lbl">{t("Stoc inițial (buc.)")}</div>
          <input type="number" min="0" value={m.stock} onChange={e=>setM(x=>({...x,stock:e.target.value}))} placeholder={t("ex: 30")}/>
        </div>
        <div><div className="lbl">{t("Alertă la (buc.)")}</div>
          <input type="number" min="0" value={m.alertAt} onChange={e=>setM(x=>({...x,alertAt:e.target.value}))} placeholder={t("ex: 5")}/>
        </div>
        <div style={{gridColumn:"span 2",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:9,padding:"10px 12px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <div style={{fontSize:13}}>{t("🔔 Notificare de terminare stoc")}</div>
            <label className="toggle" style={{cursor:"pointer"}}>
              <input type="checkbox" checked={!!m.notifyStock} onChange={e=>setM(x=>({...x,notifyStock:e.target.checked}))}/>
              <span className="slider"/>
            </label>
          </div>
          {m.notifyStock&&(
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginTop:10}}>
              <div style={{flex:1,minWidth:0,fontSize:13,color:"var(--text-2)"}}>{t("Alertă înainte cu")}</div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                <input type="number" min="1" max="30" value={m.notifyStockDays} onChange={e=>setM(x=>({...x,notifyStockDays:e.target.value}))} style={{width:44,textAlign:"center",padding:"8px 4px"}}/>
                <span style={{fontSize:13,color:"var(--text-2)"}}>{t("zile")}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Program administrare */}
      <div className="lbl" style={{marginBottom:8}}>{t("Program administrare")}</div>
      <div style={{marginBottom:12}}><Dropdown value={m.scheduleType} onChange={v=>setM(x=>({...x,scheduleType:v}))} options={SCHED_TYPES.map(({k,l})=>({v:k,l}))}/></div>

      {/* Zilnic */}
      {m.scheduleType==="daily"&&(
        <div>
          {m.schedule.map((s,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr auto",gap:8,marginBottom:8,alignItems:"center"}}>
              <div style={{fontSize:12,color:"var(--text-3)",minWidth:20,textAlign:"center"}}>{i+1}</div>
              <TimeInput value={s.time||""} onChange={v=>updSched(i,"time",v)} defaultScrollTo={DEFAULT_TIMES[i]||"09:00"}/>
              <input type="number" min="0.5" step="0.5" value={s.dose??1} onChange={e=>updSched(i,"dose",e.target.value)}/>
              {m.schedule.length>1&&<button className="bdel" onClick={()=>confirmDelete(undefined,()=>remSched(i))}><IconTrash/></button>}
              {m.schedule.length===1&&<div/>}
            </div>
          ))}
          <button className="bg" style={{fontSize:12,width:"100%",marginBottom:8,borderStyle:"dashed"}} onClick={addSched}>{t("+ Adaugă doză")}</button>
        </div>
      )}

      {/* La nevoie */}
      {m.scheduleType==="ondemand"&&(
        <div style={{padding:"10px 14px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",marginBottom:8,fontSize:13,color:"var(--text-3)"}}>
          {t("Se administrează doar la nevoie, fără orar fix.")}
        </div>
      )}

      {/* Interval ore */}
      {m.scheduleType==="interval_hours"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:8}}>
          <div><div className="lbl">{t("O dată la (ore)")}</div>
            <input type="number" min="1" max="24" value={m.intervalHours} onChange={e=>setM(x=>({...x,intervalHours:e.target.value}))}/>
          </div>
          <div><div className="lbl">{t("Prima doză la")}</div>
            <TimeInput value={m.schedule[0]?.time||"09:00"} onChange={v=>setM(x=>({...x,schedule:[{...x.schedule[0],time:v,dose:x.schedule[0]?.dose||"1"}]}))}/>
          </div>
          <div><div className="lbl">{t("Doză")}</div>
            <input type="number" min="0.5" step="0.5" value={m.schedule[0]?.dose||"1"} onChange={e=>setM(x=>({...x,schedule:[{...x.schedule[0],dose:e.target.value}]}))}/>
          </div>
        </div>
      )}

      {/* Interval zile */}
      {m.scheduleType==="interval_days"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:8}}>
          <div><div className="lbl">{t("O dată la (zile)")}</div>
            <input type="number" min="1" value={m.intervalDays} onChange={e=>setM(x=>({...x,intervalDays:e.target.value}))}/>
          </div>
          <div><div className="lbl">{t("Ora administrare")}</div>
            <TimeInput value={m.schedule[0]?.time||"09:00"} onChange={v=>setM(x=>({...x,schedule:[{...x.schedule[0],time:v,dose:x.schedule[0]?.dose||"1"}]}))}/>
          </div>
          <div><div className="lbl">{t("Doză")}</div>
            <input type="number" min="0.5" step="0.5" value={m.schedule[0]?.dose||"1"} onChange={e=>setM(x=>({...x,schedule:[{...x.schedule[0],dose:e.target.value}]}))}/>
          </div>
        </div>
      )}

      {/* Interval luni */}
      {m.scheduleType==="interval_months"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:8}}>
          <div><div className="lbl">{t("O dată la (luni)")}</div>
            <input type="number" min="1" value={m.intervalMonths} onChange={e=>setM(x=>({...x,intervalMonths:e.target.value}))}/>
          </div>
          <div><div className="lbl">{t("Doză")}</div>
            <input type="number" min="0.5" step="0.5" value={m.schedule[0]?.dose||"1"} onChange={e=>setM(x=>({...x,schedule:[{...x.schedule[0],dose:e.target.value}]}))}/>
          </div>
        </div>
      )}

      {/* Zile săptămână */}
      {m.scheduleType==="weekdays"&&(
        <div style={{marginBottom:8}}>
          <div className="lbl" style={{marginBottom:6}}>{t("Zile active")}</div>
          <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
            {WDAY_FULL.map((d,i)=>(
              <button key={d} onPointerDown={e=>{e.preventDefault();toggleWeekDay(i);}} style={{
                background:(m.weekDays||[]).includes(i)?"#7c3aed":"var(--border)",
                border:`1px solid ${(m.weekDays||[]).includes(i)?"#7c3aed":"var(--border-2)"}`,
                color:(m.weekDays||[]).includes(i)?"#fff":"var(--text-2)",
                borderRadius:7,padding:"5px 10px",cursor:"pointer",fontSize:12,fontFamily:"inherit"
              }}>{t(d)}</button>
            ))}
          </div>
          {m.schedule.map((s,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr auto",gap:8,marginBottom:8,alignItems:"center"}}>
              <div style={{fontSize:12,color:"var(--text-3)",minWidth:20,textAlign:"center"}}>{i+1}</div>
              <TimeInput value={s.time||""} onChange={v=>updSched(i,"time",v)} defaultScrollTo={DEFAULT_TIMES[i]||"09:00"}/>
              <input type="number" min="0.5" step="0.5" value={s.dose??1} onChange={e=>updSched(i,"dose",e.target.value)}/>
              {m.schedule.length>1&&<button className="bdel" onClick={()=>confirmDelete(undefined,()=>remSched(i))}><IconTrash/></button>}
              {m.schedule.length===1&&<div/>}
            </div>
          ))}
          <button className="bg" style={{fontSize:12,width:"100%",borderStyle:"dashed"}} onClick={addSched}>{t("+ Adaugă doză")}</button>
        </div>
      )}

      {/* Ciclic */}
      {m.scheduleType==="cyclic"&&(
        <div style={{marginBottom:8}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><div className="lbl">{t("Zile de administrare")}</div>
              <input type="number" min="1" value={m.cyclicOn} onChange={e=>setM(x=>({...x,cyclicOn:e.target.value}))} placeholder={t("ex: 21")}/>
            </div>
            <div><div className="lbl">{t("Zile pauză")}</div>
              <input type="number" min="1" value={m.cyclicOff} onChange={e=>setM(x=>({...x,cyclicOff:e.target.value}))} placeholder={t("ex: 7")}/>
            </div>
          </div>
          <div style={{padding:"8px 12px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",fontSize:12,color:"var(--text-3)",marginBottom:12}}>
            {m.cyclicOn} {t("zile activ")} → {m.cyclicOff} {t("zile pauză")} → {t("se repetă")}
          </div>
          {m.schedule.map((s,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"auto 1fr 1fr auto",gap:8,marginBottom:8,alignItems:"center"}}>
              <div style={{fontSize:12,color:"var(--text-3)",minWidth:20,textAlign:"center"}}>{i+1}</div>
              <TimeInput value={s.time||""} onChange={v=>updSched(i,"time",v)} defaultScrollTo={DEFAULT_TIMES[i]||"09:00"}/>
              <input type="number" min="0.5" step="0.5" value={s.dose??1} onChange={e=>updSched(i,"dose",e.target.value)}/>
              {m.schedule.length>1&&<button className="bdel" onClick={()=>confirmDelete(undefined,()=>remSched(i))}><IconTrash/></button>}
              {m.schedule.length===1&&<div/>}
            </div>
          ))}
          <button className="bg" style={{fontSize:12,width:"100%",borderStyle:"dashed"}} onClick={addSched}>{t("+ Adaugă doză")}</button>
        </div>
      )}

      <button className="bp" style={{width:"100%",marginTop:8}} onClick={()=>{if(m.name.trim()){window._formDirty=false;const schedule=[...(m.schedule||[])].sort((a,b)=>(a.time||"").localeCompare(b.time||""));onAdd({...m,schedule,stock:+m.stock||0,alertAt:+m.alertAt||0,notifyStock:!!m.notifyStock,notifyStockDays:+m.notifyStockDays||3});}}}>
        {initial?t("Salvează modificările"):t("Adaugă medicament")}
      </button>
      {onDelete&&(
        <button className="bdel" style={{width:"100%",marginTop:8,padding:"9px",fontSize:13}} onClick={onDelete}>
          <IconTrash size={14}/> {t("Șterge medicament")}
        </button>
      )}
    </div>
  );
}

export function AddMetricForm({onAdd, initial}) {
  const[date,setDate]=useState(initial?.date||todayISO());
  const[ts_sys,setS]=useState(initial?.ts_sys!=null?String(initial.ts_sys):"");
  const[ts_dia,setD]=useState(initial?.ts_dia!=null?String(initial.ts_dia):"");
  const[gli,setG]=useState(initial?.glicemie!=null?String(initial.glicemie):"");
  const[glu,setGu]=useState("mmol/L");
  const[gre,setGr]=useState(initial?.greutate!=null?String(initial.greutate):"");
  const[sat,setSat]=useState(initial?.saturatie!=null?String(initial.saturatie):"");
  const[puls,setPuls]=useState(initial?.puls!=null?String(initial.puls):"");
  const[temp,setTemp]=useState(initial?.temperatura!=null?String(initial.temperatura):"");
  return (
    <div>
      <div style={{padding:"8px 12px",background:"var(--bg)",border:"1px dashed var(--border-2)",borderRadius:8,fontSize:12,color:"var(--text-3)",marginBottom:12}}>
        ✏️ {t("Adăugare manuală — valorile din documente se extrag automat la upload.")}
      </div>
      <div style={{marginBottom:12}}><div className="lbl">{t("Data")}</div><DateField value={date} onChange={v=>setDate(v)}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <div><div className="lbl">{t("Sistolică (mmHg)")}</div><input type="number" value={ts_sys} onChange={e=>setS(e.target.value)} placeholder={t("ex: 130")}/></div>
        <div><div className="lbl">{t("Diastolică (mmHg)")}</div><input type="number" value={ts_dia} onChange={e=>setD(e.target.value)} placeholder={t("ex: 80")}/></div>
        <div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <span className="lbl" style={{margin:0}}>{t("Glicemie")}</span>
            <div style={{display:"flex",gap:3}}>
              {["mmol/L","mg/dL"].map(u=>(
                <button key={u} onClick={()=>setGu(u)} style={{background:glu===u?"#7c3aed":"none",border:`1px solid ${glu===u?"#7c3aed":"var(--border-2)"}`,color:glu===u?"#fff":"var(--text-3)",borderRadius:4,padding:"1px 6px",cursor:"pointer",fontSize:10,fontFamily:"monospace"}}>
                  {u}
                </button>
              ))}
            </div>
          </div>
          <input type="number" step="0.1" value={gli} onChange={e=>setG(e.target.value)} placeholder={glu==="mmol/L"?t("ex: 6.5"):t("ex: 117")}/>
        </div>
        <div><div className="lbl">{t("Greutate (kg)")}</div><input type="number" step="0.1" value={gre} onChange={e=>setGr(e.target.value)} placeholder={t("ex: 82")}/></div>
        <div><div className="lbl">{t("Saturație O₂ (%)")}</div><input type="number" step="1" value={sat} onChange={e=>setSat(e.target.value)} placeholder={t("ex: 98")}/></div>
        <div><div className="lbl">{t("Puls (bpm)")}</div><input type="number" step="1" value={puls} onChange={e=>setPuls(e.target.value)} placeholder={t("ex: 72")}/></div>
        <div><div className="lbl">{t("Temperatură (°C)")}</div><input type="number" step="0.1" value={temp} onChange={e=>setTemp(e.target.value)} placeholder={t("ex: 36.6")}/></div>
      </div>
      <button className="bp" style={{width:"100%"}} onClick={()=>{
        const gliVal=gli?(glu==="mg/dL"?+(+gli/18.02).toFixed(2):+gli):undefined;
        onAdd({date,ts_sys:ts_sys?+ts_sys:undefined,ts_dia:ts_dia?+ts_dia:undefined,glicemie:gliVal,greutate:gre?+gre:undefined,saturatie:sat?+sat:undefined,puls:puls?+puls:undefined,temperatura:temp?+temp:undefined});
      }}>{t("Salvează")}</button>
    </div>
  );
}

export function AddFamilyForm({initial,onAdd,checkRole}) {
  const defaultShareSettings = {fisa:true,tratament:true,analize:false,documente:false,medici:false,vaccinuri:false,familie:false,sumarAI:false};
  const isEdit = !!initial?._dbId;
  const[f,setF]=useState(initial||{name:"",relation:"Soț/Soție",dob:"",sex:"",email:"",shareAccess:false,caregiver:false,isDoctor:false,shareSettings:defaultShareSettings,createProfile:false,conditions:[]});
  const[cond,setCond]=useState("");
  function addCond(){ if(cond.trim()){setF(x=>({...x,conditions:[...(x.conditions||[]),cond.trim()]}));setCond("");} }

  // Sex automat din relație
  const SEX_FROM_RELATION = {
    "Soț":"M","Fiu":"M","Tată":"M","Bunic":"M","Frate":"M","Unchi":"M","Nepot":"M","Văr":"M",
    "Soție":"F","Fiică":"F","Mamă":"F","Bunică":"F","Soră":"F","Mătușă":"F","Nepoată":"F","Vară":"F"
  };

  function onRelationChange(rel) {
    const autoSex = SEX_FROM_RELATION[rel] || f.sex;
    setF(x=>({...x, relation:rel, sex:autoSex}));
  }

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><div className="lbl">{t("Nume")}</div><input value={f.name} onChange={e=>setF(x=>({...x,name:e.target.value}))}/></div>
        <div><div className="lbl">{t("Grad de rudenie")}</div>
          <SearchableSelect options={KINSHIP} sort={false} value={f.relation} onChange={v=>onRelationChange(v)} placeholder={t("Grad de rudenie")}/>
        </div>
        <div><div className="lbl">{t("Data nașterii")}</div><DatePicker value={f.dob||""} onChange={v=>setF(x=>({...x,dob:v}))}/></div>
        <div><div className="lbl">{t("Email")}</div><input type="email" value={f.email||""} onChange={e=>{const v=e.target.value; setF(x=>({...x,email:v,shareAccess:v.trim()?x.shareAccess:false,caregiver:v.trim()?x.caregiver:false,isDoctor:v.trim()?x.isDoctor:false}));}}/></div>
        <div style={{gridColumn:"span 2"}}>
          <div className="lbl">{t("Sex ")}<span style={{color:"var(--text-4)",fontWeight:400,textTransform:"none",letterSpacing:0}}>{t("(se completează automat din rudenie, poate fi modificat)")}</span></div>
          <div style={{display:"flex",gap:8}}>
            {[["M","Masculin"],["F","Feminin"]].map(([v,l])=>(
              <button key={v} type="button" onClick={()=>setF(x=>({...x,sex:v}))}
                style={{flex:1,padding:"7px",border:`1px solid ${f.sex===v?"#7c3aed":"var(--border-2)"}`,
                  background:f.sex===v?"var(--blue-bg)":"none",borderRadius:8,color:"var(--text)",
                  cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>
                {t(l)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <div className="lbl">{t("Condiții medicale cunoscute")}</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6,marginBottom:8}}>
          {(f.conditions||[]).map((c,i)=>(
            <span key={i} className="pill" style={{background:"var(--blue-bg)",color:"var(--blue-2)"}}>
              {c}
              <button onClick={()=>setF(x=>({...x,conditions:x.conditions.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",color:"var(--red)",cursor:"pointer",marginLeft:4,fontSize:10,padding:0}}><IconTrash/></button>
            </span>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <input value={cond} onChange={e=>setCond(e.target.value)} placeholder={t("ex: Diabet, Cancer, Hipertensiune...")} style={{flex:1}} onKeyDown={e=>e.key==="Enter"&&addCond()}/>
          <button className="bg" style={{fontSize:12,padding:"6px 12px"}} onClick={addCond}>+</button>
        </div>
      </div>

      {/* Acces și rol */}
      <div style={{marginBottom:12}}>
        {/* Partajare acces — doar dacă persoana are/creează profil */}
        {(f.profileId || f.createProfile)&&(
          <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",marginBottom:10,opacity:f.email?1:0.5}}>
            <label className="toggle" style={{marginTop:2}}>
              <input type="checkbox" checked={f.shareAccess||false} disabled={!f.email}
                onChange={e=>setF(x=>({...x,shareAccess:e.target.checked}))}/>
              <span className="slider"/>
            </label>
            <div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{t("Partajare acces")}</div>
              <div style={{fontSize:11,color:"var(--text-3)",lineHeight:1.5}}>{t("Trimite link pe email — persoana poate vedea propriul profil. AI-ul are acces la istoricul familial pentru recomandări personalizate.")}</div>
              {!f.email&&<div style={{fontSize:11,color:"var(--red)",marginTop:4}}>{t("Necesită email")}</div>}
            </div>
          </div>
        )}
        {/* Îngrijitor — apare mereu (acces la profilul curent) */}
        <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)",opacity:f.email?1:0.5}}>
          <label className="toggle" style={{marginTop:2}}>
            <input type="checkbox" checked={f.caregiver||false} disabled={!f.email}
              onChange={async e=>{
                const checked = e.target.checked;
                if(checked && checkRole && f.email){
                  try {
                    const existingRole = await checkRole(f.email);
                    if(existingRole === 'doctor'){
                      fmAlert(`${t("Adresa")} ${f.email} ${t("are deja rol de medic pentru acest profil. Nu poate fi și îngrijitor. Revocă întâi rolul de medic.")}`);
                      return; // nu activa toggle-ul
                    }
                  } catch(err) {
                    // dacă verificarea eșuează, permite activarea
                  }
                }
                setF(x=>({...x,caregiver:checked, caregiverColor: checked ? (x.caregiverColor||CAREGIVER_PALETTE[0]) : x.caregiverColor}));
              }}/>
            <span className="slider"/>
          </label>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{t("👩‍⚕️ Îngrijitor")}</div>
            <div style={{fontSize:11,color:"var(--text-3)",lineHeight:1.5}}>{t("Această persoană are acces complet la dosar — poate introduce și edita date. Se loghează cu contul ei (necesită email).")}</div>
            {!f.email&&<div style={{fontSize:11,color:"var(--red)",marginTop:4}}>{t("Necesită email")}</div>}
            {f.caregiver&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:11,color:"var(--text-2)",marginBottom:6}}>{t("Culoare badge — marcaj pe intrările introduse de")} {f.name||t("această persoană")}:</div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{width:26,height:26,borderRadius:"50%",background:f.caregiverColor||CAREGIVER_PALETTE[0],display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"var(--bg)"}}>
                    {getInitials(f.name||"?")}
                  </span>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {CAREGIVER_PALETTE.map(c=>(
                      <button key={c} type="button" onClick={()=>setF(x=>({...x,caregiverColor:c}))}
                        style={{width:22,height:22,borderRadius:"50%",background:c,cursor:"pointer",
                          border:(f.caregiverColor||CAREGIVER_PALETTE[0])===c?"2px solid #fff":"2px solid transparent"}}/>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Medicii se gestionează separat, în tab-ul „Medici" — aici doar îngrijitori. */}
      </div>

      {!isEdit&&(
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)"}}>
            <label className="toggle" style={{marginTop:2}}><input type="checkbox" checked={f.createProfile||false} onChange={e=>setF(x=>({...x,createProfile:e.target.checked}))}/><span className="slider"/></label>
            <div>
              <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{t("📋 Creează profil medical")}</div>
              <div style={{fontSize:11,color:"var(--text-3)",lineHeight:1.5}}>{t("Creează un dosar medical complet și separat pentru această persoană.")}</div>
            </div>
          </div>
        </div>
      )}
      <button className="bp" style={{width:"100%"}} onClick={()=>{if(f.name.trim())onAdd(f);}}>{t("Salvează")}</button>
    </div>
  );
}
